import React, { ChangeEvent, FC, useState } from 'react';
import { Box, CircularProgress, Container, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { SubmitButton, U1Switch, U1TextField } from 'components/atoms';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import { isNotPhoneUS, isPhoneInvalid, parsePhoneOnType } from 'utils/phoneHelpers';
import { Field, Form } from 'react-final-form';
import createDecorator from 'final-form-focus';
import TypesOfWork from '../subcontracting/TypesOfWork';
import { useFormSender } from 'hooks';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    paddingBottom: theme.spacing(7),
    paddingTop: theme.spacing(7),
  },
  container: {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(10, 5),
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
  },
  text: {
    margin: theme.spacing(3, 0, 10),
  },
  title: {
    color: theme.palette.primary.dark,
    fontWeight: 600,
  },
  divider: {
    backgroundColor: theme.palette.secondary.light,
    height: 1,
    width: '100%',
    margin: theme.spacing(7, 0),
  },
  message: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(2),
    },
    '& div': {
      maxHeight: 130,
      [theme.breakpoints.up('sm')]: {
        minHeight: 130,
      },
      '& div': {
        '& textarea': {
          minHeight: 130,
          paddingTop: theme.spacing(2),
          [theme.breakpoints.up('sm')]: {
            minHeight: 130,
          },
          '&:focus': {
            paddingTop: theme.spacing(2),
            maxHeight: 130,
            height: '130px !important',
            [theme.breakpoints.up('sm')]: {
              height: '130px !important',
            },
          },
        },
      },
    },
  },
  subTitle: {
    marginTop: theme.spacing(3),
    fontWeight: 400,
  },
  span: {
    color: theme.palette.error.main,
    fontSize: 14,
    textAlign: 'right',
    marginTop: theme.spacing(1),
  },
  spinner: {
    color: theme.palette.common.white,
  },
}));

const validator = ({
  subject = '',
  businessName = '',
  email = '',
  contactName = '',
  address = '',
  phone = '',
  satteOfIncorporation = '',
  license = '',
  employees = '',
  crewsAvailable = '',
  equipment = '',
  experience = '',
  aboutUs = '',
}) => {
  const errors: Record<string, string> = {};

  if (!isEmail(email)) errors.email = 'Invalid Email address';
  if (isEmpty(email)) errors.email = 'Required field';

  if (isEmpty(subject)) errors.subject = 'Required field';

  if (isEmpty(businessName)) errors.businessName = 'Required field';

  if (isPhoneInvalid(phone)) errors.phone = 'Invalid phone number';
  if (isNotPhoneUS(phone)) errors.phone = 'US numbers Required';

  if (isEmpty(contactName)) errors.contactName = 'Required field';
  if (isEmpty(address)) errors.address = 'Required field';
  if (isEmpty(satteOfIncorporation)) errors.satteOfIncorporation = 'Required field';
  if (isEmpty(license)) errors.license = 'Required field';
  if (isEmpty(employees)) errors.employees = 'Required field';

  if (isEmpty(crewsAvailable)) errors.crewsAvailable = 'Required field';
  if (isEmpty(equipment)) errors.equipment = 'Required field';
  if (isEmpty(experience)) errors.experience = 'Required field';
  if (isEmpty(aboutUs)) errors.aboutUs = 'Required field';

  return errors;
};

const SubcontractingFormSection: FC = () => {
  const classes = useStyles();
  const [jobs, setJobs] = useState('No');
  const [requirements, setRequirements] = useState('No');
  const [travel, setTravel] = useState('No');
  const [typesOfWork, setTypesOfWork] = useState<any>({});
  const [errorText, setCheckErrorText] = useState<any>('');
  const [checkedJob, setCheckedJob] = useState(false);
  const [checkedRequirements, setCheckedRequirements] = React.useState(false);
  const [checkedTravel, setCheckedTravel] = useState(false);
  const [expandedConstruction, setExpandedConstruction] = useState<string | false>('panel1');
  const [expandedEngineering, setExpandedEngineering] = useState<string | false>('panel1');
  const focusOnError = createDecorator();

  const scrollToTopConstruction = () => {
    const hiddenElement = document.getElementById('titleConstruction');
    hiddenElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const scrollToTopEngineering = () => {
    const hiddenElement = document.getElementById('titleEngineering');
    hiddenElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const handleChangeConstruction = (panel: string) => (event: ChangeEvent, newExpanded: boolean) => {
    setExpandedConstruction(newExpanded ? panel : false);
    scrollToTopConstruction();
  };

  const handleChangeEngineering = (panel: string) => (event: ChangeEvent, newExpanded: boolean) => {
    setExpandedEngineering(newExpanded ? panel : false);
    scrollToTopEngineering();
  };

  const {
    onSubmit,
    status: { error, loading, success },
  } = useFormSender('/api/subcontracting');

  const onChangeJobs = (event) => {
    setCheckedJob(event.target.checked);
    setJobs(checkedJob ? jobs && 'No' : jobs && 'Yes');
  };

  const onChangeRequirements = (event) => {
    setCheckedRequirements(event.target.checked);
    setRequirements(checkedRequirements ? requirements && 'No' : requirements && 'Yes');
  };

  const onChangeTravel = (event) => {
    setCheckedTravel(event.target.checked);
    setTravel(checkedTravel ? travel && 'No' : travel && 'Yes');
  };

  const onChangeTypesOfWork = (value) => {
    setTypesOfWork(value);
  };

  const handleSubmit = async (values, actions) => {
    await onSubmit(values, actions, undefined, { typesOfWork: JSON.stringify(typesOfWork), jobs, requirements, travel });
    setTypesOfWork({});
    setCheckedJob(false);
    setCheckedRequirements(false);
    setCheckedTravel(false);
    setExpandedEngineering(false);
    setExpandedConstruction(false);
  };

  return (
    <Box className={classes.root}>
      <Container fixed className={classes.container}>
        <Typography variant="h3">Contractors for Utilities One</Typography>
        <Typography className={classes.text}>
          Utilities One also utilizes specialty subcontractors for certain phases of telecommunications construction. If you are a
          contractor, please complete and submit the Subcontractors Information sheet below in order to be considered for future
          projects.
        </Typography>
        <Form
          validate={validator}
          decorators={[focusOnError]}
          onSubmit={handleSubmit}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={5}>
                <Grid item lg={3} xs={12}>
                  <Typography variant="h5" className={classes.title}>
                    Business Overview
                  </Typography>
                </Grid>
                <Grid item lg={9} xs={12}>
                  <Field name={'subject'}>
                    {({ input, meta: { error, touched } }) => (
                      <U1TextField
                        label="Subject*"
                        {...input}
                        disabled={submitting}
                        helperText={touched && error}
                        error={touched && !!error}
                      />
                    )}
                  </Field>
                </Grid>
                <Box className={classes.divider} />
                <Grid item lg={3} xs={12}>
                  <Typography variant="h5" className={classes.title}>
                    Company
                  </Typography>
                </Grid>
                <Grid item lg={9} xs={12}>
                  <Grid container spacing={5}>
                    <Grid item lg={6} xs={12}>
                      <Field name={'businessName'}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="Business Name*"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item lg={6} xs={12}>
                      <Field name={'email'}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="Email*"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item lg={6} xs={12}>
                      <Field name={'website'}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="Website"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item lg={6} xs={12}>
                      <Field name={'contactName'}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="Contact Name*"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item lg={6} xs={12}>
                      <Field name={'address'}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="Address*"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item lg={6} xs={12}>
                      <Field name={'phone'} parse={parsePhoneOnType}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="Phone*"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                  </Grid>
                </Grid>
                <Box className={classes.divider} />
                <Grid item lg={3} xs={12}>
                  <Typography variant="h5" className={classes.title}>
                    Business Sizes and Credentials
                  </Typography>
                </Grid>
                <Grid item lg={9} xs={12}>
                  <Grid container spacing={5}>
                    <Grid item lg={6} xs={12}>
                      <Field name={'satteOfIncorporation'}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="State of incorporation*"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item lg={6} xs={12}>
                      <Field name={'license'}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="States you're licensed to serve*"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item lg={4} xs={12}>
                      <Field name={'employees'}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="Number of employees*"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item lg={4} xs={12}>
                      <Field name={'crewsAvailable'}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="Number of crews available*"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item lg={4} xs={12}>
                      <Field name={'equipment'}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="Type(s) and age of equipment*"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                  </Grid>
                </Grid>
                <Box className={classes.divider} />

                <Grid item lg={3} xs={12}>
                  <Typography variant="h5" className={classes.title}>
                    Other
                  </Typography>
                </Grid>
                <Grid item lg={8} xs={12}>
                  <U1Switch
                    text="Have you ever worked for Utilities One before?"
                    onChange={onChangeJobs}
                    value={jobs}
                    checked={checkedJob}
                  />
                  <Box mb={7} />
                  <U1Switch
                    text="Subcontractors must obtain & maintain specific types & limits of insurance. Does the company have the ability to fulfill our insurance requirements?"
                    onChange={onChangeRequirements}
                    value={requirements}
                    checked={checkedRequirements}
                  />
                  <Box mb={7} />
                  <U1Switch text="Are you willing to travel?" onChange={onChangeTravel} value={travel} checked={checkedTravel} />
                </Grid>
                <Box className={classes.divider} />
                <Grid item lg={3} xs={12}>
                  <Typography variant="h5" className={classes.title}>
                    Types of work you do
                  </Typography>
                  <Typography variant="h6" className={classes.subTitle}>
                    Position Type
                  </Typography>
                </Grid>
                <Grid item lg={9} xs={12}>
                  <TypesOfWork
                    expandedConstruction={expandedConstruction}
                    onChangeConstruction={handleChangeConstruction}
                    onChangeTypesOfWork={onChangeTypesOfWork}
                    typesOfWork={typesOfWork}
                    expandedEngineering={expandedEngineering}
                    onChangeEngineering={handleChangeEngineering}
                  />
                  <Box display="flex" justifyContent="flex-end">
                    <span className={classes.span}>{errorText}</span>
                  </Box>
                </Grid>
                <Box className={classes.divider} />
                <Grid item lg={3} xs={12}>
                  <Typography variant="h5" className={classes.title}>
                    Submit application
                  </Typography>
                </Grid>
                <Grid item lg={9} xs={12}>
                  <Grid container spacing={5}>
                    <Grid item lg={6} xs={12}>
                      <Field name={'experience'}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="Experience and prior jobs*"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item lg={6} xs={12}>
                      <Field name={'aboutUs'}>
                        {({ input, meta: { error, touched } }) => (
                          <U1TextField
                            label="How did you find out about us?*"
                            {...input}
                            disabled={submitting}
                            helperText={touched && error}
                            error={touched && !!error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item lg={6} xs={12}>
                      <Box className={classes.message}>
                        <Field name={'questions'}>
                          {({ input, meta: { error, touched } }) => (
                            <U1TextField
                              {...input}
                              label="Comments and questions"
                              disabled={submitting}
                              multiline
                              helperText={touched && error}
                              error={touched && !!error}
                            />
                          )}
                        </Field>
                      </Box>
                    </Grid>
                    <Grid item lg={6} xs={12}>
                      <Box className={classes.message}>
                        <Field name={'message'}>
                          {({ input, meta: { error, touched } }) => (
                            <U1TextField
                              {...input}
                              label="Message"
                              disabled={submitting}
                              multiline
                              helperText={touched && error}
                              error={touched && !!error}
                            />
                          )}
                        </Field>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid item lg={12}>
                    <Box mt={6} />
                    <SubmitButton disabled={submitting} error={error} success={success}>
                      {loading && <CircularProgress size="30px" className={classes.spinner} />}
                      {success && 'Thank you for filling out your information!'}
                      {error && 'An error occurred. Please try again.'}
                      {!loading && !success && !error && 'Send'}
                    </SubmitButton>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          )}
        />
      </Container>
    </Box>
  );
};

export default SubcontractingFormSection;
